import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Stack,
  Button,
  Avatar,
  Paper,
  ToggleButton,
  ToggleButtonGroup,
  TextField,
  IconButton,
  InputAdornment,
  Alert,
} from '@mui/material';
import LogoutRounded from '@mui/icons-material/LogoutRounded';
import LightModeOutlined from '@mui/icons-material/LightModeOutlined';
import DarkModeOutlined from '@mui/icons-material/DarkModeOutlined';
import PersonRounded from '@mui/icons-material/PersonRounded';
import PaletteRounded from '@mui/icons-material/PaletteRounded';
import LockRounded from '@mui/icons-material/LockRounded';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { useNavigate } from 'react-router-dom';
import { ModeContext } from '../../theme/ThemeProvider';
import { clearAuthStorage } from '../../utils/auth';
import SettingsPageHeader from './components/SettingsPageHeader';
import { SnackbarContext } from '../../ui/SnackbarProvider';
import { buildApiUrl, tenantApiPrefix } from '../../config';
import { getTenantIdFromStorage } from '../../utils/onboardingLocalStorage';

const cardSx = { mb: 3 };
const cardContentSx = {
  px: { xs: 2, sm: 3 },
  py: { xs: 2, sm: 3 },
  '&:last-child': { pb: { xs: 2, sm: 3 } },
};

interface SectionHeaderProps {
  icon: React.ReactNode;
  title: string;
  subtitle?: string;
}

function SectionHeader({ icon, title, subtitle }: SectionHeaderProps) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 2 }}>
      <Box
        sx={(t) => ({
          width: 32,
          height: 32,
          borderRadius: `${t.ds.radius.sm}px`,
          bgcolor: t.tokens.brand.primarySoft,
          color: t.tokens.brand.primary,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        })}
      >
        {icon}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="subtitle1" fontWeight={700} sx={{ fontSize: '1rem', lineHeight: 1.3 }}>
          {title}
        </Typography>
        {subtitle ? (
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8125rem', lineHeight: 1.45, mt: 0.25 }}>
            {subtitle}
          </Typography>
        ) : null}
      </Box>
    </Box>
  );
}

const getInitials = (input?: string): string => {
  if (!input) return '?';
  const parts = input.split(/[\s@.]+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[1]![0]!).toUpperCase();
};

export default function AccountSettingsPage() {
  const navigate = useNavigate();
  const { mode, setMode } = React.useContext(ModeContext);
  const { notify } = React.useContext(SnackbarContext);
  const [user, setUser] = React.useState<{ name?: string; email?: string }>({});
  const [tenant, setTenant] = React.useState<{ name?: string; plan?: string }>({});
  // hasPassword tracks the server-side state. We seed from
  // localStorage (the login response includes has_password now) and
  // refresh from /users/me on mount so the section copy is correct
  // even if the user opened settings in a different tab after setting
  // a password elsewhere.
  const [hasPassword, setHasPassword] = React.useState<boolean>(false);
  const [pwOpen, setPwOpen] = React.useState(false);
  const [currentPw, setCurrentPw] = React.useState('');
  const [newPw, setNewPw] = React.useState('');
  const [confirmPw, setConfirmPw] = React.useState('');
  const [pwError, setPwError] = React.useState<string | null>(null);
  const [pwSubmitting, setPwSubmitting] = React.useState(false);
  const [showCurrent, setShowCurrent] = React.useState(false);
  const [showNew, setShowNew] = React.useState(false);

  const token = React.useMemo(() => window.localStorage.getItem('klaris_jwt') || '', []);

  React.useEffect(() => {
    try {
      const u = JSON.parse(String(window.localStorage.getItem('klaris_user') || 'null')) || {};
      setUser({ name: u?.name || u?.full_name, email: u?.email });
      setHasPassword(Boolean(u?.has_password));
      const t = JSON.parse(String(window.localStorage.getItem('klaris_tenant') || 'null')) || {};
      setTenant({ name: t?.name, plan: t?.plan });
    } catch {
      /* ignore */
    }

    (async () => {
      try {
        const tid = getTenantIdFromStorage();
        if (!tid || !token) return;
        const resp = await fetch(buildApiUrl(`${tenantApiPrefix(tid)}/users/me`), {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (resp.ok) {
          const data = await resp.json();
          setHasPassword(Boolean(data?.has_password));
        }
      } catch {
        // Non-fatal — we still have the localStorage seed.
      }
    })();
  }, [token]);

  const resetPwForm = () => {
    setCurrentPw('');
    setNewPw('');
    setConfirmPw('');
    setPwError(null);
    setShowCurrent(false);
    setShowNew(false);
  };

  const handlePasswordSubmit = async () => {
    setPwError(null);
    if (newPw.length < 8) {
      setPwError('Password must be at least 8 characters.');
      return;
    }
    if (newPw !== confirmPw) {
      setPwError('Passwords do not match.');
      return;
    }
    if (hasPassword && !currentPw) {
      setPwError('Enter your current password to confirm.');
      return;
    }
    setPwSubmitting(true);
    try {
      const tid = getTenantIdFromStorage();
      if (!tid) throw new Error('Not authenticated');
      const body: Record<string, string> = { new_password: newPw };
      if (hasPassword) body.current_password = currentPw;
      const resp = await fetch(buildApiUrl(`${tenantApiPrefix(tid)}/users/me/password`), {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(data?.detail || 'Failed to update password');
      setHasPassword(true);
      // Mirror the change into the cached user object so other pages
      // see the right `has_password` until next login.
      try {
        const stored = JSON.parse(String(window.localStorage.getItem('klaris_user') || 'null')) || {};
        stored.has_password = true;
        window.localStorage.setItem('klaris_user', JSON.stringify(stored));
      } catch {
        /* ignore */
      }
      notify(hasPassword ? 'Password updated' : 'Password set', 'success');
      setPwOpen(false);
      resetPwForm();
    } catch (e: any) {
      setPwError(e?.message || 'Failed to update password');
    } finally {
      setPwSubmitting(false);
    }
  };

  const handleSignOut = () => {
    clearAuthStorage();
    navigate('/login');
  };

  return (
    <Box sx={{ width: '100%' }}>
      <SettingsPageHeader description="Personal preferences for your account on this device." />

      {/* Profile */}
      <Card sx={cardSx}>
        <CardContent sx={cardContentSx}>
          <SectionHeader
            icon={<PersonRounded sx={{ fontSize: 18 }} />}
            title="Profile"
            subtitle="Information shown next to your name across the product."
          />
          <Paper
            variant="outlined"
            sx={(t) => ({
              p: { xs: 2, sm: 2.5 },
              borderRadius: `${t.ds.radius.md}px`,
              borderColor: t.tokens.border.subtle,
              bgcolor: t.tokens.surface.sunken,
            })}
          >
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              alignItems={{ xs: 'flex-start', sm: 'center' }}
            >
              <Avatar
                sx={(t) => ({
                  width: 56,
                  height: 56,
                  fontSize: '1.125rem',
                  fontWeight: 700,
                  bgcolor: t.tokens.brand.primary,
                  color: t.tokens.brand.onPrimary,
                })}
              >
                {getInitials(user.name || user.email)}
              </Avatar>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography variant="h6" fontWeight={700} sx={{ fontSize: '1rem', lineHeight: 1.3, wordBreak: 'break-word' }}>
                  {user.name || user.email || 'You'}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8125rem', wordBreak: 'break-all', mt: 0.25 }}>
                  {user.email || '—'}
                </Typography>
                {tenant.name ? (
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem', mt: 0.5 }}>
                    Member of <strong>{tenant.name}</strong>
                    {tenant.plan ? <> · {tenant.plan} plan</> : null}
                  </Typography>
                ) : null}
              </Box>
            </Stack>
          </Paper>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', mt: 1.5, fontSize: '0.75rem' }}
          >
            To update your name or email, contact your organization owner.
          </Typography>
        </CardContent>
      </Card>

      {/* Password */}
      <Card sx={cardSx}>
        <CardContent sx={cardContentSx}>
          <SectionHeader
            icon={<LockRounded sx={{ fontSize: 18 }} />}
            title="Password"
            subtitle={
              hasPassword
                ? 'You can sign in with your email + password, or with an email code.'
                : 'You currently sign in with an email code. Set a password to also sign in with email + password.'
            }
          />

          {!pwOpen ? (
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              alignItems={{ xs: 'stretch', sm: 'center' }}
              justifyContent="space-between"
            >
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8125rem' }}>
                {hasPassword ? 'Password is set.' : 'No password set.'}
              </Typography>
              <Button
                variant="outlined"
                onClick={() => {
                  resetPwForm();
                  setPwOpen(true);
                }}
                sx={{ textTransform: 'none', fontWeight: 600 }}
              >
                {hasPassword ? 'Change password' : 'Set password'}
              </Button>
            </Stack>
          ) : (
            <Stack spacing={2}>
              {pwError ? <Alert severity="error">{pwError}</Alert> : null}
              {hasPassword ? (
                <TextField
                  label="Current password"
                  type={showCurrent ? 'text' : 'password'}
                  value={currentPw}
                  onChange={(e) => setCurrentPw(e.target.value)}
                  autoComplete="current-password"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle current password visibility"
                          onClick={() => setShowCurrent((v) => !v)}
                          edge="end"
                          size="small"
                        >
                          {showCurrent ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              ) : null}
              <TextField
                label="New password"
                type={showNew ? 'text' : 'password'}
                value={newPw}
                onChange={(e) => setNewPw(e.target.value)}
                autoComplete="new-password"
                helperText="At least 8 characters."
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle new password visibility"
                        onClick={() => setShowNew((v) => !v)}
                        edge="end"
                        size="small"
                      >
                        {showNew ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                label="Confirm new password"
                type={showNew ? 'text' : 'password'}
                value={confirmPw}
                onChange={(e) => setConfirmPw(e.target.value)}
                autoComplete="new-password"
              />
              <Stack direction="row" spacing={1} justifyContent="flex-end">
                <Button
                  onClick={() => {
                    setPwOpen(false);
                    resetPwForm();
                  }}
                  disabled={pwSubmitting}
                  sx={{ textTransform: 'none', fontWeight: 600 }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={handlePasswordSubmit}
                  disabled={pwSubmitting}
                  sx={{ textTransform: 'none', fontWeight: 600 }}
                >
                  {hasPassword ? 'Update password' : 'Set password'}
                </Button>
              </Stack>
            </Stack>
          )}
        </CardContent>
      </Card>

      {/* Appearance */}
      <Card sx={cardSx}>
        <CardContent sx={cardContentSx}>
          <SectionHeader
            icon={<PaletteRounded sx={{ fontSize: 18 }} />}
            title="Appearance"
            subtitle="Choose how the app looks on this device. Saved locally — applies instantly."
          />
          <ToggleButtonGroup
            value={mode}
            exclusive
            onChange={(_e, val: 'light' | 'dark' | null) => {
              if (val) setMode(val);
            }}
            sx={{ width: { xs: '100%', sm: 'auto' } }}
          >
            <ToggleButton
              value="light"
              sx={{
                px: 3,
                py: 1.25,
                textTransform: 'none',
                flex: { xs: 1, sm: 'none' },
                gap: 1,
                fontWeight: 600,
              }}
            >
              <LightModeOutlined sx={{ fontSize: 18 }} />
              Light
            </ToggleButton>
            <ToggleButton
              value="dark"
              sx={{
                px: 3,
                py: 1.25,
                textTransform: 'none',
                flex: { xs: 1, sm: 'none' },
                gap: 1,
                fontWeight: 600,
              }}
            >
              <DarkModeOutlined sx={{ fontSize: 18 }} />
              Dark
            </ToggleButton>
          </ToggleButtonGroup>
        </CardContent>
      </Card>

      {/* Sign out */}
      <Card sx={cardSx}>
        <CardContent sx={cardContentSx}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            justifyContent="space-between"
            alignItems={{ xs: 'stretch', sm: 'center' }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="subtitle1" fontWeight={700} sx={{ fontSize: '1rem' }}>
                Sign out
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8125rem', mt: 0.25 }}>
                You will be returned to the login screen on this device.
              </Typography>
            </Box>
            <Button
              variant="outlined"
              color="error"
              startIcon={<LogoutRounded sx={{ fontSize: 18 }} />}
              onClick={handleSignOut}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              Sign out
            </Button>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
}
